import React, { MutableRefObject, Suspense } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import AdminTrialCart from "@atlassiansox/admin-trial-cart";

import { Adg4Button, ButtonColor } from "@/components/adg4/button/src";
import { GlobalNavUserProfile } from "@/components/global-nav/user-profile-new/src";
import { TryIntent } from "@/components/try-intent/src/try-intent.tsx";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { useLoggedInUser } from "../../../utils/logged-in-user-provider";

import SearchButton from "./search-button";

export type SearchAndProfileProps = {
    isShowTryNowLink?: boolean;
    ctaButtonColor?: ButtonColor;
    productKey?: ProductKeyTypes;
    isShowSearchButton?: boolean;
    setIsOpenSearch: React.Dispatch<React.SetStateAction<boolean>>;
    searchAndProfileRef: MutableRefObject<HTMLDivElement | null>;
    navigationTrynowCtaText?: string;
    navigationTrynowCtaUrl?: string;
    navigationTrynowCtaOnClick?: (e: React.MouseEvent) => void;
    isDisplayDivider?: boolean;
};

const dividerStyle = css({
    borderInlineStart: "1px solid #dfe1e5",
    width: "1px",
    height: "24px",
    alignSelf: "center"
});

const appSwitcherContainerStyle = css({
    marginInlineEnd: "16px"
});

const navigationCartButtonStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    listStyle: "none",
    margin: 0,
    padding: 0
});

const navigationActionStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    listStyle: "none",
    margin: 0,
    padding: 0
});

const navigationActionLinkStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "row"
});

const WacAppSwitcher = React.lazy(() =>
    // without the line ts-ignored below TSC throws this error:
    // "Relative import paths need explicit file extensions in EcmaScript imports when '--moduleResolution' is 'node16' or 'nodenext'. Did you mean '../../../components/global-nav/app-switcher/src/index.js'?"
    // the file of course ends in .tsx, when you try to add that there is a different error:
    // "An import path can only end with a '.tsx' extension when 'allowImportingTsExtensions' is enabled."

    // @ts-ignore
    import("../../../components/global-nav/app-switcher/src/index").then(
        (module) => ({
            default: module.WacAppSwitcher
        })
    )
);

const SearchAndProfile = ({
    isShowTryNowLink = false,
    ctaButtonColor = "blue",
    productKey = "home",
    isShowSearchButton = true,
    setIsOpenSearch,
    navigationTrynowCtaText,
    navigationTrynowCtaUrl,
    navigationTrynowCtaOnClick,
    searchAndProfileRef,
    isDisplayDivider = true
}: SearchAndProfileProps) => {
    const intl = useIntl();
    /* TODO: Implement setIsUserLoggedIn, user logged in status (in useEffect), once GWP-51 in implemented  */
    const { isUserLoggedIn } = useLoggedInUser();
    const defaultCTAUrl = useLocalizedHref("/try");
    const buttonText =
        navigationTrynowCtaText ??
        intl.formatMessage({
            id: "globalnav.v2.try-now-button-label",
            defaultMessage: "Try Now"
        });
    const buttonUrl = navigationTrynowCtaOnClick
        ? undefined
        : navigationTrynowCtaUrl ?? defaultCTAUrl;

    return (
        <div
            css={css({
                alignSelf: "end",
                display: "flex",
                flexDirection: "row",
                gap: "16px"
            })}
            ref={searchAndProfileRef}
            className="search-and-profile"
        >
            {isShowTryNowLink && (
                <TryIntent intentType={"button"} containerId={"navbar"}>
                    <div
                        className="navigation-trynow"
                        css={css({
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        })}
                    >
                        <Adg4Button
                            variant="primary"
                            color={ctaButtonColor}
                            href={buttonUrl}
                            size="small"
                            dataEventProps={{
                                "data-product-key": productKey,
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "navbar",
                                "data-label": buttonText,
                                "data-label-english": "Get it free",
                                "data-track-event": `${productKey}.get-it-free-button-clicked`
                            }}
                            onClick={navigationTrynowCtaOnClick}
                        >
                            {buttonText}
                        </Adg4Button>
                    </div>
                </TryIntent>
            )}
            <div
                className="navigation-search"
                css={css({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                })}
            >
                {isShowSearchButton && (
                    <SearchButton
                        onClick={(e) => {
                            e.preventDefault();
                            setIsOpenSearch(true);
                        }}
                    />
                )}
            </div>
            {isDisplayDivider ? (
                <div
                    className="navigation-switcher-divider"
                    css={dividerStyle}
                />
            ) : null}
            <ul className="navigation-action" css={navigationActionStyle}>
                <li css={navigationActionLinkStyle}>
                    {isUserLoggedIn && (
                        <Suspense>
                            <div css={appSwitcherContainerStyle}>
                                <WacAppSwitcher
                                    allowJoinSitesProvider={true}
                                    appSwitcherLabel={""}
                                    appSwitcherIconPrimaryColor={"#42526e"}
                                    isUseV2AppSwitcherStyle={true}
                                    isDisplayBorderAndHoverEffect={false}
                                />
                            </div>
                        </Suspense>
                    )}
                </li>
                <li css={navigationActionLinkStyle}>
                    <GlobalNavUserProfile isUseNewLoggedInUserDropdown={true} />
                </li>
                <li
                    className="navigation-cart-button"
                    css={navigationCartButtonStyle}
                >
                    <AdminTrialCart version={0} fields={{}} />
                </li>
            </ul>
        </div>
    );
};

export default SearchAndProfile;
